import API_BASE_URL from '../../config/api';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Input from '../../components/base/Input';
import Button from '../../components/base/Button';
import JsonViewer from '../../components/base/JsonViewer';
import { AuthContext } from '../../context/AuthContext';
import MessageModal from '../../components/base/MessageModal';
import { getReservedPdfClientError } from '../../utils/reservedPdfClients';

// Interfaces
interface Field {
  id: string;
  name: string;
  description: string;
}

interface SubSection {
  id: string;
  name: string;
  fields: Field[];
}

interface Section {
  id: string;
  name: string;
  fields: Field[];
  subSections: SubSection[];
}

type PostprocessPreset = { label: string; prompt: string };
type PostprocessPresetGroup = { title: string; presets: PostprocessPreset[] };
type ManagedPostprocessPreset = { id: string; label: string; prompt: string };

const POSTPROCESS_PRESET_GROUPS: PostprocessPresetGroup[] = [
  {
    title: "Modify Text Fields",
    presets: [
      { label: "Combine 'ExtraItemDesc' before 'ItemDesc'", prompt: "For each item in 'ItemsDetails', set 'ItemDesc' = 'ExtraItemDesc' + ' ' + 'ItemDesc'. Keep all existing fields." },
      { label: "Append 'ExtraItemDesc' after 'ItemDesc'", prompt: "For each item in 'ItemsDetails', set 'ItemDesc' = 'ItemDesc' + ' ' + 'ExtraItemDesc'. Keep all existing fields." },
      { label: "Add 'PART NO:' with 'ExtraItemDesc'", prompt: "For each item in 'ItemsDetails', set 'ItemDesc' = 'ItemDesc' + ' PART NO: ' + 'ExtraItemDesc'. Keep all existing fields." },
      { label: "Prefix 'ItemDesc' with 'PONumber'", prompt: "For each item in 'ItemsDetails', set 'ItemDesc' = 'PONumber' + ' ' + 'ItemDesc'. Keep all existing fields." },
      { label: "Set 'ItemDesc' = 'PartNo-ItemDesc-PONumber'", prompt: "For each item in 'ItemsDetails', set 'ItemDesc' = 'PartNo' + '-' + 'ItemDesc' + '-' + 'PONumber'. Keep all existing fields." },
      { label: "Set 'ItemDesc' with default word suffix", prompt: "For each item in 'ItemsDetails', set 'ItemDesc' = 'ItemDesc' + 'Default Word'. Keep all existing fields." },
      { label: "Set 'ItemDesc' with default word prefix", prompt: "For each item in 'ItemsDetails', set 'ItemDesc' = 'Default Word' + 'ItemDesc'. Keep all existing fields." },
    ],
  },
  {
    title: "Clean Data",
    presets: [
      { label: "Remove text inside parentheses from 'ItemDesc'", prompt: "For each item in 'ItemsDetails', remove any text inside parentheses including '(' and ')' from 'ItemDesc', then trim extra spaces. Keep all existing fields and do not remove any key." },
    ],
  },
  {
    title: "Calculations",
    presets: [
      { label: "Calculate 'ItemNETWEIGHT' from totals and quantity", prompt: "For each item in 'ItemsDetails', set 'ItemNETWEIGHT' = ('TotalNetWeight' / 'TotalQuantity') * 'Quantity'. Use available totals from header/invoice when present. Keep all existing fields." },
      { label: "Set 'ItemNETWEIGHT' by 'ItemDesc' rules", prompt: "For each item in 'ItemsDetails': if normalized 'ItemDesc' equals 'ZIP FASTENERS FOR SHOE', set 'ItemNETWEIGHT' = float('Quantity') / 100. If normalized 'ItemDesc' equals 'RING FOR SHOE', set 'ItemNETWEIGHT' = float('Quantity') * 0.00013. Keep all existing fields and do not remove any key." },
      { label: "Calculate 'Rate' as Amount / Quantity", prompt: "For each item in 'ItemsDetails', set 'Rate' = 'Amount' / 'Quantity' when both are present and quantity is not zero. Keep all existing fields." },
      { label: "Calculate 'Amount' as Quantity * Rate", prompt: "For each item in 'ItemsDetails', set 'Amount' = 'Quantity' * 'Rate' when both are present. Keep all existing fields." },
    ],
  },
];

export default function CreateFromExisting() {
  const navigate = useNavigate();
  const location = useLocation() as { state?: { filename?: string; fromAdmin?: boolean; userid?: string; pdfclientname?: string; jobtype?: string; company_id?: string } };
  const auth = useContext(AuthContext);
  const [username, setUsername] = useState<string | null>(auth?.username || localStorage.getItem('username'));
  const [companyId, setCompanyId] = useState<string>('');
  const [isAdmin, setIsAdmin] = useState<boolean>(() => {
    const role = localStorage.getItem('userRole') || 'user';
    return role === 'admin' || role === 'super_admin';
  });
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(() => {
    const role = localStorage.getItem('userRole') || 'user';
    return role === 'super_admin';
  });
  const [selectedParser, setSelectedParser] = useState('');
  useEffect(() => {
    const preset = location?.state?.filename;
    if (preset) {
      setSelectedParser(preset);
    }
    // If admin came from Admin Panel, default the working username to selected user's id
    if (location?.state?.fromAdmin && location.state.userid) {
      setUsername(location.state.userid);
    }
    // If admin came from Admin Panel, set the company_id
    if (location?.state?.fromAdmin && location.state.company_id) {
      setCompanyId(location.state.company_id);
    }
  }, [location]);
  // Parser name removed; filenames are derived from pdfclientname and jobtype
  const [documentType, setDocumentType] = useState('');
  // Admin-only extra fields
  const [adminUserId, setAdminUserId] = useState<string>(location?.state?.userid || (username || ''));
  const [adminPdfClientName, setAdminPdfClientName] = useState<string>(location?.state?.pdfclientname || '');
  const [adminJobType, setAdminJobType] = useState<string>(location?.state?.jobtype || '');
  const [sections, setSections] = useState<Section[]>([]);
  const [rootFields, setRootFields] = useState<Field[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [reservedModal, setReservedModal] = useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  });
  const [jsonPreviewKey, setJsonPreviewKey] = useState(0); // Add key to force JSON preview refresh
  const [additionalRemarks, setAdditionalRemarks] = useState<string>(''); // System instruction field
  const [postprocessPrompt, setPostprocessPrompt] = useState<string>('');
  const [postprocessSource, setPostprocessSource] = useState<string>('');
  const [showPresetPrompts, setShowPresetPrompts] = useState(false);
  const [managedPostprocessPresets, setManagedPostprocessPresets] = useState<ManagedPostprocessPreset[]>([]);
  const [isLoadingManagedPresets, setIsLoadingManagedPresets] = useState(false);
  const [managedPresetError, setManagedPresetError] = useState('');
  const [newManagedPresetLabel, setNewManagedPresetLabel] = useState('');
  const [newManagedPresetPrompt, setNewManagedPresetPrompt] = useState('');
  const [isSavingManagedPreset, setIsSavingManagedPreset] = useState(false);
  const [isDeletingManagedPresetId, setIsDeletingManagedPresetId] = useState<string | null>(null);
  const [existingPostprocessPresent, setExistingPostprocessPresent] = useState(false); // true when parser has saved postprocess (we do not show the code)
  const [postprocessSaveStatus, setPostprocessSaveStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [postprocessError, setPostprocessError] = useState<string>('');
  const [savedModelName, setSavedModelName] = useState<string | null>(null);
  const [savedModelDisplay, setSavedModelDisplay] = useState<string | null>(null);
  interface OptionItem { value: string; label: string; }
  const [options, setOptions] = useState<OptionItem[]>([]);
  const effectiveUsername = useMemo(() => {
    if (isAdmin && adminUserId && adminUserId.trim()) return adminUserId.trim();
    return username || '';
  }, [isAdmin, adminUserId, username]);

  useEffect(() => {
    const loadUsername = async () => {
      if (auth?.username) { 
        setUsername(auth.username); 
        return; 
      }
      const stored = localStorage.getItem('username');
      if (stored) setUsername(stored);
    };
    loadUsername();
    const role = localStorage.getItem('userRole') || 'user';
    setIsAdmin(role === 'admin' || role === 'super_admin');
    setIsSuperAdmin(role === 'super_admin');
  }, [auth]);

  const loadManagedPostprocessPresets = async () => {
    setIsLoadingManagedPresets(true);
    setManagedPresetError('');
    try {
      const resp = await fetch(`${API_BASE_URL}/postprocess-prompt-presets`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });
      const data = await resp.json();
      if (data.success) {
        setManagedPostprocessPresets(Array.isArray(data.presets) ? data.presets : []);
      } else {
        setManagedPresetError(data.error || 'Failed to load managed prompts.');
      }
    } catch (e) {
      setManagedPresetError(e instanceof Error ? e.message : 'Failed to load managed prompts.');
    } finally {
      setIsLoadingManagedPresets(false);
    }
  };

  useEffect(() => {
    loadManagedPostprocessPresets();
  }, []);

  // Update username when auth context changes
  useEffect(() => {
    if (auth?.username) {
      setUsername(auth.username);
    }
  }, [auth?.username]);

  useEffect(() => {
    const loadCompanyId = async () => {
      try {
        const currentUsername = auth?.username || localStorage.getItem('username');
        if (currentUsername) {
          const response = await fetch(`${API_BASE_URL}/admin/users`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
            },
          });
          const data = await response.json();
          if (data.success && Array.isArray(data.users)) {
            const currentUser = data.users.find((user: any) => user.userid === currentUsername);
            if (currentUser && currentUser.company_id) {
              // Only set company ID if we're not coming from admin panel
              // (admin panel already sets the correct company ID from selected user)
              if (!location?.state?.fromAdmin) {
                setCompanyId(currentUser.company_id);
              }
            }
          }
        }
      } catch (error) {
        console.error('Failed to load company ID:', error);
      }
    };
    loadCompanyId();
  }, [auth, location?.state?.fromAdmin]);

  // Update adminUserId when username changes
  useEffect(() => {
    if (username && !location?.state?.fromAdmin) {
      setAdminUserId(username);
    }
  }, [username, location?.state?.fromAdmin]);

  // Trigger JSON preview refresh when form fields change
  useEffect(() => {
    setJsonPreviewKey(prev => prev + 1);
  }, [adminPdfClientName, adminJobType, companyId, sections, rootFields]);

  useEffect(() => {
    const loadParsers = async () => {
      try {
        const url = `${API_BASE_URL}/list-parsers`;
        const userResp = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
          },
        });
        const userData = await userResp.json();
        const userOnes: string[] = Array.isArray(userData.parsers) ? userData.parsers : [];

        const filtered = userOnes.filter((p) => p.endsWith('.json'));

        // Map to display labels - keep full path in dropdown
        const opts: OptionItem[] = filtered.map((full) => {
          return { value: full, label: full };
        });

        // De-duplicate by value while preserving first occurrence
        const seen = new Set<string>();
        const unique = opts.filter((o) => (seen.has(o.value) ? false : (seen.add(o.value), true)));
        setOptions(unique);
      } catch (e) {
        console.error('load parsers failed', e);
      }
    };
    loadParsers();
    const t = setTimeout(loadParsers, 0);
    const onFocus = () => loadParsers();
    const onStorage = (e: StorageEvent) => { if (e.key === 'token') loadParsers(); };
    window.addEventListener('focus', onFocus);
    window.addEventListener('storage', onStorage);
    return () => {
      clearTimeout(t);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('storage', onStorage);
    };
  }, [effectiveUsername]);

  useEffect(() => {
    const loadSelected = async () => {
      if (!selectedParser) return;
      try {
        const qs = new URLSearchParams({
          name: selectedParser,
          username: effectiveUsername || '',
        }).toString();
        // const resp = await fetch(`/api/get-parser?${qs}`, {
        const resp = await fetch(`${API_BASE_URL}/get-parser?${qs}`, {

          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
          },
        });
        const data = await resp.json();
        if (data.success && data.parser) {
          const p = data.parser;
          // Handle both structures: { document_type, schema } and direct schema structure
          const docType = p.document_type || '';
          const schema = p.schema || p; // Use p.schema if exists, otherwise use p directly
          setDocumentType(docType);
          
          // Load system instruction if available
          if (data.systemInstruction) {
            setAdditionalRemarks(data.systemInstruction);
          } else {
            setAdditionalRemarks('');
          }
          // Load postprocess: do not show saved code in UI; show saved prompt if present
          if (data.postprocessSource) {
            setExistingPostprocessPresent(true);
            setPostprocessSource(''); // do not display saved code
          } else {
            setExistingPostprocessPresent(false);
            setPostprocessSource('');
          }
          if (data.postprocessPrompt != null && data.postprocessPrompt !== undefined) {
            setPostprocessPrompt(String(data.postprocessPrompt));
          } else {
            setPostprocessPrompt('');
          }
          // Prefill for two filename styles:
          // 1) client_job.json -> sets client & job
          // 2) defaults like imp.json/exp.json -> set jobtype = filename (no underscore)
          let base = selectedParser;
          let extractedCompanyId = '';
          
          // Extract company ID if the parser name contains a company_id prefix (e.g., "ER0022/dvision_exp.json")
          if (base.includes('/')) {
            const pathParts = base.split('/');
            if (pathParts.length >= 2) {
              extractedCompanyId = pathParts[0]; // Extract company ID (e.g., "ER0022")
              base = pathParts[1]; // Extract filename (e.g., "dvision_exp.json")
            } else {
              base = base.split('/').pop() || base;
            }
          }
          
          if (base.endsWith('.json')) base = base.slice(0, -5);
          const parts = base.split('_');
          if (parts.length >= 2) {
            const inferredClient = parts[0];
            const inferredJob = parts[1];
            // Always update the fields when a parser is selected
            setAdminPdfClientName(inferredClient);
            setAdminJobType(inferredJob);
            // Set company ID from the selected parser if available
            if (extractedCompanyId) {
              setCompanyId(extractedCompanyId);
            }
            // Trigger JSON preview refresh
            setJsonPreviewKey(prev => prev + 1);
          } else if (parts.length === 1) {
            setAdminJobType(parts[0]);
            // Set company ID from the selected parser if available
            if (extractedCompanyId) {
              setCompanyId(extractedCompanyId);
            }
            // Trigger JSON preview refresh
            setJsonPreviewKey(prev => prev + 1);
          }
          
          // Check for saved generation function
          try {
            const checkResponse = await fetch(`${API_BASE_URL}/check-generation-function?parser_name=${encodeURIComponent(selectedParser)}`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
              },
            });
            const checkData = await checkResponse.json();
            if (checkData.success) {
              setSavedModelName(checkData.model_name);
              setSavedModelDisplay(checkData.model_display);
            } else {
              setSavedModelName(null);
              setSavedModelDisplay(null);
            }
          } catch (error) {
            console.error('Error checking generation function:', error);
            setSavedModelName(null);
            setSavedModelDisplay(null);
          }
          
          // transform schema into rootFields/sections, reading descriptions from schema values
          const newRoot: Field[] = [];
          const newSections: Section[] = [];
          Object.entries(schema as Record<string, any>).forEach(([key, value]) => {
            if (value && typeof value === 'object') {
              if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
                // Handle default parser structure: { "SectionName": [{ "field1": "description", "field2": "description" }] }
                const sectionFields: Field[] = Object.entries(value[0]).map(([fieldName, fieldValue]) => ({
                  id: generateId(),
                  name: fieldName,
                  description: typeof fieldValue === 'string' ? fieldValue : ''
                }));
                newSections.push({ id: generateId(), name: key, fields: sectionFields, subSections: [] });
              } else if (!Array.isArray(value)) {
                // Handle user-created parser structure: { "SectionName": { "field1": "description", "field2": "description" } }
                const sectionFields: Field[] = [];
                const subSections: SubSection[] = [];
                Object.entries(value as Record<string, any>).forEach(([sKey, sVal]) => {
                  if (sVal && typeof sVal === 'object' && !Array.isArray(sVal)) {
                    // subsection
                    const subFields: Field[] = Object.entries(sVal as Record<string, any>).map(([name, v]) => ({
                      id: generateId(),
                      name,
                      description: typeof v === 'string' ? v : ''
                    }));
                    subSections.push({ id: generateId(), name: sKey, fields: subFields });
                  } else {
                    sectionFields.push({ id: generateId(), name: sKey, description: typeof sVal === 'string' ? sVal : '' });
                  }
                });
                newSections.push({ id: generateId(), name: key, fields: sectionFields, subSections });
              }
            } else {
              newRoot.push({ id: generateId(), name: key, description: typeof value === 'string' ? value : '' });
            }
          });
          setRootFields(newRoot);
          setSections(newSections);
        }
      } catch (e) {
        console.error('load selected failed', e);
      }
    };
    loadSelected();
  }, [selectedParser, effectiveUsername]);

  const generateId = () => Math.random().toString(36).substr(2, 9);

  const addSection = () => {
    const newSection: Section = {
      id: generateId(),
      name: '',
      fields: [],
      subSections: []
    };
    setSections([...sections, newSection]);
  };

  const addSubSection = (sectionId: string) => {
    setSections(sections.map(section => {
      if (section.id === sectionId) {
        const newSubSection: SubSection = {
          id: generateId(),
          name: '',
          fields: []
        };
        return { ...section, subSections: [...section.subSections, newSubSection] };
      }
      return section;
    }));
  };

  const addField = (sectionId?: string, subSectionId?: string) => {
    const newField: Field = {
      id: generateId(),
      name: '',
      description: ''
    };

    if (!sectionId && !subSectionId) {
      // Add to root level
      setRootFields([...rootFields, newField]);
    } else if (sectionId && !subSectionId) {
      // Add to section
      setSections(sections.map(section => {
        if (section.id === sectionId) {
          return { ...section, fields: [...section.fields, newField] };
        }
        return section;
      }));
    } else if (sectionId && subSectionId) {
      // Add to subsection
      setSections(sections.map(section => {
        if (section.id === sectionId) {
          return {
            ...section,
            subSections: section.subSections.map(subSection => {
              if (subSection.id === subSectionId) {
                return { ...subSection, fields: [...subSection.fields, newField] };
              }
              return subSection;
            })
          };
        }
        return section;
      }));
    }
  };

  const deleteSection = (sectionId: string) => {
    setSections(sections.filter(section => section.id !== sectionId));
  };

  const deleteSubSection = (sectionId: string, subSectionId: string) => {
    setSections(sections.map(section => {
      if (section.id === sectionId) {
        return {
          ...section,
          subSections: section.subSections.filter(subSection => subSection.id !== subSectionId)
        };
      }
      return section;
    }));
  };

  const deleteField = (fieldId: string, sectionId?: string, subSectionId?: string) => {
    if (!sectionId && !subSectionId) {
      // Delete from root level
      setRootFields(rootFields.filter(field => field.id !== fieldId));
    } else if (sectionId && !subSectionId) {
      // Delete from section
      setSections(sections.map(section => {
        if (section.id === sectionId) {
          return { ...section, fields: section.fields.filter(field => field.id !== fieldId) };
        }
        return section;
      }));
    } else if (sectionId && subSectionId) {
      // Delete from subsection
      setSections(sections.map(section => {
        if (section.id === sectionId) {
          return {
            ...section,
            subSections: section.subSections.map(subSection => {
              if (subSection.id === subSectionId) {
                return { ...subSection, fields: subSection.fields.filter(field => field.id !== fieldId) };
              }
              return subSection;
            })
          };
        }
        return section;
      }));
    }
  };

  const updateSectionName = (sectionId: string, name: string) => {
    setSections(sections.map(section => 
      section.id === sectionId ? { ...section, name } : section
    ));
  };

  const updateSubSectionName = (sectionId: string, subSectionId: string, name: string) => {
    setSections(sections.map(section => {
      if (section.id === sectionId) {
        return {
          ...section,
          subSections: section.subSections.map(subSection =>
            subSection.id === subSectionId ? { ...subSection, name } : subSection
          )
        };
      }
      return section;
    }));
  };

  const updateField = (fieldId: string, name: string, description: string, sectionId?: string, subSectionId?: string) => {
    if (!sectionId && !subSectionId) {
      // Update root level field
      setRootFields(rootFields.map(field => 
        field.id === fieldId ? { ...field, name, description } : field
      ));
    } else if (sectionId && !subSectionId) {
      // Update section field
      setSections(sections.map(section => {
        if (section.id === sectionId) {
          return {
            ...section,
            fields: section.fields.map(field =>
              field.id === fieldId ? { ...field, name, description } : field
            )
          };
        }
        return section;
      }));
    } else if (sectionId && subSectionId) {
      // Update subsection field  
      setSections(sections.map(section => {
        if (section.id === sectionId) {
          return {
            ...section,
            subSections: section.subSections.map(subSection => {
              if (subSection.id === subSectionId) {
                return {
                  ...subSection,
                  fields: subSection.fields.map(field =>
                    field.id === fieldId ? { ...field, name, description } : field
                  )
                };
              }
              return subSection;
            })
          };
        }
        return section;
      }));
    }
  };

  const generateJsonSchema = () => {
    const schema: Record<string, any> = {};
    
    // Add root level fields
    rootFields.forEach(field => {
      if (field.name) {
        schema[field.name] = field.description || '';
      }
    });

    // Add sections
    sections.forEach(section => {
      if (section.name) {
        // Check if this is a default parser structure (has fields but no subsections)
        if (section.fields.length > 0 && section.subSections.length === 0) {
          // For default parsers, create array structure with object containing all fields
          const fieldObject: Record<string, string> = {};
          section.fields.forEach(field => {
            if (field.name) {
              fieldObject[field.name] = field.description || '';
            }
          });
          schema[section.name] = [fieldObject];
        } else {
          // For user-created parsers, use the original structure
          const sectionSchema: Record<string, any> = {};
          
          // Add section fields
          section.fields.forEach(field => {
            if (field.name) {
              sectionSchema[field.name] = field.description || '';
            }
          });

          // Add subsections
          section.subSections.forEach(subSection => {
            if (subSection.name) {
              const subSectionSchema: Record<string, string> = {};
              subSection.fields.forEach(field => {
                if (field.name) {
                  subSectionSchema[field.name] = field.description || '';
                }
              });
              sectionSchema[subSection.name] = subSectionSchema;
            }
          });

          schema[section.name] = sectionSchema;
        }
      }
    });
    
    return schema;
  };

  const validateFields = () => {
    // Admin-only required fields
    if (isAdmin) {
      if (!adminUserId.trim()) { alert('Please enter username'); return false; }
      if (!adminPdfClientName.trim()) { alert('Please enter PDF client name'); return false; }
      if (!adminJobType.trim()) { alert('Please enter job type'); return false; }
    } else {
      // For users, pdfclientname and jobtype are required
      if (!adminPdfClientName.trim()) { alert('Please enter PDF client name'); return false; }
      if (!adminJobType.trim()) { alert('Please enter job type'); return false; }
    }
    
    const hasFields = rootFields.some(field => field.name.trim()) || 
                     sections.some(section => 
                       section.fields.some(field => field.name.trim()) ||
                       section.subSections.some(subSection => 
                         subSection.fields.some(field => field.name.trim())
                       )
                     );
    
    if (!hasFields) {
      alert('Please add at least one field');
      return false;
    }
    
    return true;
  };

  const getParserNameForPostprocess = () => {
    // Prefer form fields (adminPdfClientName, adminJobType) - these define the parser being created/edited and match save-parser
    if (adminPdfClientName?.trim() && adminJobType?.trim()) {
      const base = `${adminPdfClientName.trim()}_${adminJobType.trim()}.json`;
      return companyId?.trim() ? `${companyId.trim()}/${base}` : base;
    }
    if (selectedParser?.trim()) return selectedParser.trim();
    return null;
  };

  const handleGenerateSavePostprocess = async () => {
    const parserName = getParserNameForPostprocess();
    if (!parserName) {
      setPostprocessError('Select or create a parser first (PDF client name and job type).');
      setPostprocessSaveStatus('error');
      return;
    }
    if (!postprocessPrompt.trim()) {
      setPostprocessError('Enter a prompt describing the post-processing logic.');
      setPostprocessSaveStatus('error');
      return;
    }
    setPostprocessSaveStatus('loading');
    setPostprocessError('');
    try {
      const response = await fetch(`${API_BASE_URL}/generate-save-postprocess`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({
          parser_name: parserName,
          prompt: postprocessPrompt.trim(),
          schema_keys: sections.length || rootFields.length ? [...rootFields.map(f => f.name), ...sections.map(s => s.name)] : undefined,
        }),
      });
      const result = await response.json();
      if (result.success) {
        setPostprocessSaveStatus('success');
        setPostprocessError('');
        setTimeout(() => setPostprocessSaveStatus('idle'), 3000);
      } else {
        setPostprocessSaveStatus('error');
        setPostprocessError(result.error || 'Failed to generate or save postprocess.');
      }
    } catch (err) {
      setPostprocessSaveStatus('error');
      setPostprocessError(err instanceof Error ? err.message : 'Network error.');
    }
  };

  const handleSavePostprocessSource = async () => {
    const parserName = getParserNameForPostprocess();
    if (!parserName) {
      setPostprocessError('Select or create a parser first.');
      setPostprocessSaveStatus('error');
      return;
    }
    if (!postprocessSource.trim()) {
      setPostprocessError('Enter or generate postprocess code first.');
      setPostprocessSaveStatus('error');
      return;
    }
    setPostprocessSaveStatus('loading');
    setPostprocessError('');
    try {
      const response = await fetch(`${API_BASE_URL}/generate-save-postprocess`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({ parser_name: parserName, source: postprocessSource.trim() }),
      });
      const result = await response.json();
      if (result.success) {
        setPostprocessSaveStatus('success');
        setPostprocessError('');
        setTimeout(() => setPostprocessSaveStatus('idle'), 3000);
      } else {
        setPostprocessSaveStatus('error');
        setPostprocessError(result.error || 'Failed to save postprocess.');
      }
    } catch (err) {
      setPostprocessSaveStatus('error');
      setPostprocessError(err instanceof Error ? err.message : 'Network error.');
    }
  };

  const handleUsePresetPrompt = (prompt: string) => {
    setPostprocessPrompt((prev) => {
      const current = (prev || "").trim();
      if (!current) return prompt;
      return `${current}\n${prompt}`;
    });
    setPostprocessError('');
    if (postprocessSaveStatus !== 'loading') {
      setPostprocessSaveStatus('idle');
    }
  };

  const handleClearPostprocessPrompt = () => {
    setPostprocessPrompt('');
    setPostprocessError('');
    if (postprocessSaveStatus !== 'loading') {
      setPostprocessSaveStatus('idle');
    }
  };

  const handleAddManagedPreset = async () => {
    const label = newManagedPresetLabel.trim();
    const prompt = newManagedPresetPrompt.trim();
    if (!label || !prompt) {
      setManagedPresetError('Enter both label and prompt.');
      return;
    }
    setIsSavingManagedPreset(true);
    setManagedPresetError('');
    try {
      const resp = await fetch(`${API_BASE_URL}/admin/postprocess-prompt-presets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({ label, prompt }),
      });
      const data = await resp.json();
      if (data.success && data.preset) {
        setManagedPostprocessPresets((prev) => [...prev, data.preset]);
        setNewManagedPresetLabel('');
        setNewManagedPresetPrompt('');
      } else {
        setManagedPresetError(data.error || 'Failed to save prompt.');
      }
    } catch (e) {
      setManagedPresetError(e instanceof Error ? e.message : 'Failed to save prompt.');
    } finally {
      setIsSavingManagedPreset(false);
    }
  };

  const handleDeleteManagedPreset = async (presetId: string) => {
    setIsDeletingManagedPresetId(presetId);
    setManagedPresetError('');
    try {
      const resp = await fetch(`${API_BASE_URL}/admin/postprocess-prompt-presets/${encodeURIComponent(presetId)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });
      const data = await resp.json();
      if (data.success) {
        setManagedPostprocessPresets((prev) => prev.filter((p) => p.id !== presetId));
      } else {
        setManagedPresetError(data.error || 'Failed to delete prompt.');
      }
    } catch (e) {
      setManagedPresetError(e instanceof Error ? e.message : 'Failed to delete prompt.');
    } finally {
      setIsDeletingManagedPresetId(null);
    }
  };

  const handleSave = async () => {
    if (!validateFields()) return;
    
    setIsLoading(true);
    setSuccessMessage('');
    
    try {
      const reservedError = await getReservedPdfClientError(adminPdfClientName, adminJobType);
      if (reservedError) {
        setReservedModal({ open: true, message: reservedError });
        return;
      }

      const jsonSchema = generateJsonSchema();
      const fileName = `${adminPdfClientName}_${adminJobType}.json`;
      // For both admin and user, composite name is built in backend. Provide fields explicitly.
      const finalDocumentType = (adminJobType || documentType || '').trim();
      const targetUsername = (isAdmin && adminUserId.trim()) ? adminUserId.trim() : (username || '');
      // const response = await fetch('/api/save-parser', {
      const response = await fetch(`${API_BASE_URL}/save-parser`, {

        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({ 
          parserFileName: fileName, 
          documentType: finalDocumentType, 
          jsonSchema, 
          username: targetUsername, 
          pdfclientname: adminPdfClientName || undefined, 
          jobtype: adminJobType || undefined,
          company_id: companyId && companyId.trim() ? companyId.trim() : 
                       (isAdmin && location?.state?.fromAdmin && location.state.company_id) ? location.state.company_id : undefined,
          systemInstruction: additionalRemarks && additionalRemarks.trim() ? additionalRemarks.trim() : undefined
        }),
      });
      const result = await response.json();
      if (result.success) {
        setSuccessMessage('Parser saved successfully');
        setTimeout(() => {
          setSuccessMessage('');
          navigate('/parser-creation');
        }, 1500);
      } else {
        const errMsg = result.error || 'Failed to save parser';
        if (/reserved/i.test(errMsg)) {
          setReservedModal({ open: true, message: errMsg });
        } else {
          alert(errMsg);
        }
      }
    } catch (error) {
      console.error('Save error:', error);
      alert('Failed to save parser. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Render fields section (modified)
  const renderFieldsSection = (fields: Field[], sectionId?: string, subSectionId?: string) => {
    if (fields.length === 0) return null;

    return (
      <div className="space-y-4">
        {/* Header row - shown only once for the first field */}
        <div className="grid grid-cols-2 gap-4 pb-2 border-b border-gray-200">
          <div className="text-base font-medium text-indigo-600">Fields</div>
          <div className="text-base font-medium text-indigo-600">Description</div>
        </div>
        
        {/* Field rows */}
        {fields.map((field) => {
          const isSystemNumberField =
            field.name === 'Itemslno' ||
            field.name === 'ItemSlNo' ||
            field.name === 'Containerslno' ||
            field.name === 'InvoiceSlNo';

          return (
            <div key={field.id} className="grid grid-cols-2 gap-4 items-center">
              <input
                type="text"
                placeholder="Field name"
                value={field.name}
                readOnly={!isAdmin}
                onChange={
                  isAdmin
                    ? (e) =>
                        updateField(
                          field.id,
                          e.target.value,
                          field.description,
                          sectionId,
                          subSectionId
                        )
                    : undefined
                }
                className={
                  isAdmin
                    ? "w-full px-4 py-3 border-2 border-cyan-400 rounded-lg focus:outline-none focus:border-indigo-600 transition-colors text-base"
                    : "w-full px-4 py-3 border-2 border-gray-200 bg-gray-100 rounded-lg text-base cursor-not-allowed"
                }
              />
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder={
                    isSystemNumberField
                      ? "Description is fixed for this field"
                      : "Enter field description"
                  }
                  value={field.description}
                  readOnly={isSystemNumberField}
                  onChange={
                    isSystemNumberField
                      ? undefined
                      : (e) =>
                          updateField(
                            field.id,
                            field.name,
                            e.target.value,
                            sectionId,
                            subSectionId
                          )
                  }
                  className={
                    isSystemNumberField
                      ? "w-full px-4 py-3 border-2 border-gray-200 bg-gray-100 rounded-lg text-base cursor-not-allowed"
                      : "w-full px-4 py-3 border-2 border-cyan-400 rounded-lg focus:outline-none focus:border-indigo-600 transition-colors text-base"
                  }
                />
                {isAdmin && (
                  <button
                    onClick={() => deleteField(field.id, sectionId, subSectionId)}
                    className="w-8 h-8 flex items-center justify-center text-red-500 hover:text-red-700 cursor-pointer flex-shrink-0 disabled:opacity-50"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50">
      <Navbar showUserActions={true} />
      
      <div className="px-4 py-6">
        <Button variant="outline" onClick={() => navigate('/parser-creation')} className="mb-6">
          Back
        </Button>
      </div>
      
      {successMessage && (
        <div className="fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50">
          <div className="flex items-center gap-2">
            <i className="ri-check-line"></i>
            {successMessage}
          </div>
        </div>
      )}
      
      <div className="flex flex-col items-center px-6">
        <div className="w-full max-w-7xl">
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-gray-800 mb-4">Create from Existing Parser</h1>
            <div className="w-full h-px bg-gray-300"></div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Left Side - Form */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-indigo-600 mb-2">
                    Select Existing Parser
                  </label>
                  <select 
                    value={selectedParser}
                    onChange={(e) => {
                      setSelectedParser(e.target.value);
                      // Clear saved model info when parser changes
                      setSavedModelName(null);
                      setSavedModelDisplay(null);
                      // Clear form fields when changing selection
                      if (!e.target.value) {
                        setAdminPdfClientName('');
                        setAdminJobType('');
                        setSections([]);
                        setRootFields([]);
                        setAdditionalRemarks('');
                        // Reset company ID to admin's own company ID when no parser is selected
                        if (isAdmin) {
                          setCompanyId('ER0001'); // Admin's company ID
                        }
                        setJsonPreviewKey(prev => prev + 1);
                      } else {
                        // Clear additional remarks when switching to a different parser
                        setAdditionalRemarks('');
                      }
                    }}
                    className="w-full px-4 py-3 border-2 border-cyan-400 rounded-lg focus:outline-none focus:border-indigo-600 transition-colors text-base pr-8"
                  >
                    <option value="">Choose a parser</option>
                    {options.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  {selectedParser && (
                    <p className="text-sm text-gray-600 mt-2">
                      Selected: {selectedParser}
                    </p>
                  )}
                </div>
                
                {/* Rename Parser removed */}

                {/* Admin-only: company_id (editable), pdfclientname, jobtype; hide doc type for admin */}
                {isAdmin ? (
                  <>
                    <Input
                      placeholder="Company ID"
                      label="Company ID"
                      value={companyId}
                      onChange={(e) => setCompanyId(e.target.value)}
                    />
                    <Input
                      placeholder="Enter PDF client name"
                      label="PDF Client Name"
                      value={adminPdfClientName}
                      onChange={(e) => setAdminPdfClientName(e.target.value)}
                    />
                    <Input
                      placeholder="Enter job type"
                      label="Job Type"
                      value={adminJobType}
                      onChange={(e) => setAdminJobType(e.target.value)}
                    />
                  </>
                ) : (
                  <>
                    <Input
                      placeholder="Enter PDF client name"
                      label="PDF Client Name"
                      value={adminPdfClientName}
                      onChange={(e) => setAdminPdfClientName(e.target.value)}
                    />
                    <Input
                      placeholder="Enter job type"
                      label="Job Type"
                      value={adminJobType}
                      onChange={(e) => setAdminJobType(e.target.value)}
                    />
                  </>
                )}
                
                {/* Parser Instruction Guideline Message */}
                {selectedParser && savedModelDisplay && (
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <i className="ri-information-line text-blue-500 text-xl"></i>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-blue-700">
                          <span className="font-semibold">You are using </span>
                          <span className="font-bold text-blue-900">
                            {(() => {
                              let parserDisplayName = selectedParser;
                              // Remove .json extension if present
                              if (parserDisplayName.endsWith('.json')) {
                                parserDisplayName = parserDisplayName.slice(0, -5);
                              }
                              // If parser name doesn't include company ID, add it
                              if (!parserDisplayName.includes('/')) {
                                const currentCompanyId = companyId || (selectedParser.includes('/') ? selectedParser.split('/')[0] : '');
                                if (currentCompanyId) {
                                  parserDisplayName = `${currentCompanyId.toUpperCase()}/${parserDisplayName}`;
                                }
                              }
                              return parserDisplayName;
                            })()}
                          </span>
                          <span className="font-semibold"> with </span>
                          <span className="font-bold text-blue-900">
                            {savedModelName === 'gemini-2.5-flash' ? 'advanced model tier' : 'standard model tier'}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Additional Remarks / System Instruction */}
                <div>
                  <label className="block text-sm font-medium text-indigo-600 mb-2">
                    Parser Instruction Guideline
                  </label>
                  <textarea
                    placeholder="Enter custom instruction."
                    value={additionalRemarks}
                    onChange={(e) => setAdditionalRemarks(e.target.value)}
                    rows={6}
                    className="w-full px-4 py-3 border-2 border-cyan-400 rounded-lg focus:outline-none focus:border-indigo-600 transition-colors text-base resize-y"
                  />
                </div>

                {/* Post-processing logic (prompt → model generates Python, saved to bucket; applied after extraction) */}
                <div className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50">
                  <label className="block text-sm font-medium text-indigo-600 mb-2">
                    Post-processing logic
                  </label>
                  <p className="text-sm text-gray-600 mb-2">
                    Describe calculations, swaps, concatenation, or stripping to apply to extracted data. Mention field names in single quotes (e.g. 'ItemDesc', 'Amount'). This will generate code and save it for this parser. It runs automatically when you test documents.
                  </p>
                  <textarea
                    placeholder="e.g. Set 'TaxableAmount' = 'Amount'/1.18 for each item; replace KG with KGS in 'ItemDesc'."
                    value={postprocessPrompt}
                    onChange={(e) => setPostprocessPrompt(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border-2 border-cyan-400 rounded-lg focus:outline-none focus:border-indigo-600 transition-colors text-base resize-y mb-2"
                  />
                  <div className="flex items-center gap-2 mb-2">
                    <button
                      type="button"
                      onClick={handleClearPostprocessPrompt}
                      className="px-3 py-1.5 text-xs rounded-lg border border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
                      title="Clear prompt"
                    >
                      <i className="ri-eraser-line mr-1"></i>Clear
                    </button>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={handleGenerateSavePostprocess}
                      disabled={postprocessSaveStatus === 'loading'}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium"
                    >
                      {postprocessSaveStatus === 'loading' ? 'Generating…' : 'Generate & save postprocess'}
                    </button>
                    {postprocessSaveStatus === 'success' && (
                      <span className="text-green-600 text-sm">Saved.</span>
                    )}
                    {postprocessSaveStatus === 'error' && postprocessError && (
                      <span className="text-red-600 text-sm">{postprocessError}</span>
                    )}
                  </div>
                  {existingPostprocessPresent && (
                    <p className="mt-2 text-sm text-gray-500">Post-processing code is already saved for this parser. Add a prompt above to generate new code.</p>
                  )}
                  <div className="mt-3 border border-gray-200 rounded-lg bg-white">
                    <button
                      type="button"
                      onClick={() => setShowPresetPrompts((prev) => !prev)}
                      className="w-full px-3 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center justify-between"
                    >
                      <span><i className="ri-lightbulb-line mr-1"></i>Help</span>
                      <i className={`ri-arrow-${showPresetPrompts ? 'up' : 'down'}-s-line`}></i>
                    </button>
                    {showPresetPrompts && (
                      <div className="px-3 pb-3 border-t border-gray-100 max-h-64 overflow-y-auto">
                        {POSTPROCESS_PRESET_GROUPS.map((group) => (
                          <div key={group.title} className="mt-3">
                            <p className="text-sm font-semibold text-indigo-700 mb-2">
                              <i className="ri-chat-1-line mr-1"></i>{group.title}
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {group.presets.map((preset) => (
                                <button
                                  key={preset.label}
                                  type="button"
                                  onClick={() => handleUsePresetPrompt(preset.prompt)}
                                  className="px-3 py-1.5 text-xs rounded-full border border-cyan-200 bg-cyan-50 text-gray-700 hover:bg-cyan-100"
                                  title={preset.prompt}
                                >
                                  <i className="ri-corner-down-right-line mr-1"></i>{preset.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                        <div className="mt-3">
                          <p className="text-sm font-semibold text-indigo-700 mb-2">
                            <i className="ri-folder-settings-line mr-1"></i>Managed Prompts
                          </p>
                          {isLoadingManagedPresets ? (
                            <p className="text-xs text-gray-500">Loading…</p>
                          ) : managedPostprocessPresets.length === 0 ? (
                            <p className="text-xs text-gray-500">No managed prompts yet.</p>
                          ) : (
                            <div className="flex flex-wrap gap-2">
                              {managedPostprocessPresets.map((preset) => (
                                <div key={preset.id} className="inline-flex items-center gap-1">
                                  <button
                                    type="button"
                                    onClick={() => handleUsePresetPrompt(preset.prompt)}
                                    className="px-3 py-1.5 text-xs rounded-full border border-cyan-200 bg-cyan-50 text-gray-700 hover:bg-cyan-100"
                                    title={preset.prompt}
                                  >
                                    <i className="ri-corner-down-right-line mr-1"></i>{preset.label}
                                  </button>
                                  {isSuperAdmin && (
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteManagedPreset(preset.id)}
                                      disabled={isDeletingManagedPresetId === preset.id}
                                      className="w-6 h-6 text-xs rounded-full border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50"
                                      title="Delete prompt"
                                    >
                                      <i className="ri-delete-bin-line"></i>
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        {isSuperAdmin && (
                          <div className="mt-4 p-3 border border-gray-200 rounded-lg bg-gray-50">
                            <p className="text-sm font-semibold text-indigo-700 mb-2">
                              <i className="ri-shield-user-line mr-1"></i>Add Managed Prompt
                            </p>
                            <input
                              type="text"
                              placeholder="Prompt label"
                              value={newManagedPresetLabel}
                              onChange={(e) => setNewManagedPresetLabel(e.target.value)}
                              className="w-full mb-2 px-3 py-2 border border-cyan-300 rounded-lg text-sm focus:outline-none focus:border-indigo-600"
                            />
                            <textarea
                              placeholder="Prompt text (mention fields in single quotes, e.g. 'ItemDesc')"
                              value={newManagedPresetPrompt}
                              onChange={(e) => setNewManagedPresetPrompt(e.target.value)}
                              rows={3}
                              className="w-full px-3 py-2 border border-cyan-300 rounded-lg text-sm focus:outline-none focus:border-indigo-600 resize-y"
                            />
                            <div className="mt-2 flex items-center gap-2">
                              <button
                                type="button"
                                onClick={handleAddManagedPreset}
                                disabled={isSavingManagedPreset}
                                className="px-3 py-1.5 text-xs rounded-lg border border-indigo-200 bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                              >
                                {isSavingManagedPreset ? 'Saving…' : 'Save prompt'}
                              </button>
                              <button
                                type="button"
                                onClick={loadManagedPostprocessPresets}
                                className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-100"
                              >
                                Refresh
                              </button>
                            </div>
                          </div>
                        )}
                        {managedPresetError && (
                          <p className="mt-2 text-xs text-red-600">{managedPresetError}</p>
                        )}
                      </div>
                    )}
                  </div>
                  {/* Generated code section hidden when parser already has postprocess; only show after generating in this session */}
                  {postprocessSource && !existingPostprocessPresent && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Generated code (optional edit and save)</label>
                      <textarea
                        placeholder="Python postprocess function"
                        value={postprocessSource}
                        onChange={(e) => setPostprocessSource(e.target.value)}
                        rows={8}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg font-mono text-sm resize-y"
                      />
                      <button
                        type="button"
                        onClick={handleSavePostprocessSource}
                        disabled={postprocessSaveStatus === 'loading'}
                        className="mt-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 text-sm"
                      >
                        Save postprocess code
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Root Level Fields */}
                {renderFieldsSection(rootFields)}
                {isAdmin && (
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => addField()}
                      className="flex items-center gap-2 px-4 py-2 text-cyan-500 hover:text-cyan-600 cursor-pointer"
                    >
                      <i className="ri-add-line"></i>
                      <span className="text-base">Add Field</span>
                    </button>
                  </div>
                )}

                {/* Sections */}
                {sections.map((section) => (
                  <div key={section.id} className="border-2 border-gray-300 rounded-lg p-5">
                    <div className="flex justify-between items-center mb-4">
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          placeholder="Section Name"
                          value={section.name}
                          readOnly={!isAdmin}
                          onChange={isAdmin ? (e) => updateSectionName(section.id, e.target.value) : undefined}
                          className={
                            isAdmin
                              ? "px-3 py-1 border-2 border-gray-300 rounded focus:outline-none focus:border-indigo-600 text-base"
                              : "px-3 py-1 border-2 border-gray-200 bg-gray-100 rounded text-base cursor-not-allowed"
                          }
                        />
                        <span className="bg-indigo-600 text-white px-2 py-1 rounded text-xs">Section</span>
                      </div>
                      {isAdmin && (
                        <button
                          onClick={() => deleteSection(section.id)}
                          className="w-6 h-6 flex items-center justify-center text-red-500 hover:text-red-700 cursor-pointer disabled:opacity-50"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      )}
                    </div>

                    {/* Section Fields */}
                    {renderFieldsSection(section.fields, section.id)}

                    {/* Sub-sections */}
                    {section.subSections.map((subSection) => (
                      <div key={subSection.id} className="ml-6 border-l-2 border-gray-200 pl-4 mb-4 mt-4">
                        <div className="flex justify-between items-center mb-4">
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              placeholder="Sub-section Name"
                              value={subSection.name}
                              readOnly={!isAdmin}
                              onChange={isAdmin ? (e) => updateSubSectionName(section.id, subSection.id, e.target.value) : undefined}
                              className={
                                isAdmin
                                  ? "px-3 py-1 border-2 border-gray-300 rounded focus:outline-none focus:border-indigo-600 text-base"
                                  : "px-3 py-1 border-2 border-gray-200 bg-gray-100 rounded text-base cursor-not-allowed"
                              }
                            />
                          </div>
                          {isAdmin && (
                            <button
                              onClick={() => deleteSubSection(section.id, subSection.id)}
                              className="w-6 h-6 flex items-center justify-center text-red-500 hover:text-red-700 cursor-pointer disabled:opacity-50"
                            >
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          )}
                        </div>

                        {renderFieldsSection(subSection.fields, section.id, subSection.id)}
                        {isAdmin && (
                          <button
                            onClick={() => addField(section.id, subSection.id)}
                            className="flex items-center gap-2 px-4 py-2 text-cyan-500 hover:text-cyan-600 cursor-pointer mt-4 disabled:opacity-50"
                          >
                            <i className="ri-add-line"></i>
                            <span className="text-base">Add Field</span>
                          </button>
                        )}
                      </div>
                    ))}
                    {isAdmin && (
                      <div className="flex flex-wrap gap-2 mt-4">
                        <button
                          onClick={() => addField(section.id)}
                          className="flex items-center gap-2 px-4 py-2 text-cyan-500 hover:text-cyan-600 cursor-pointer disabled:opacity-50"
                        >
                          <i className="ri-add-line"></i>
                          <span className="text-base">Add Field</span>
                        </button>
                        <button
                          onClick={() => addSubSection(section.id)}
                          className="flex items-center gap-2 px-4 py-2 text-cyan-500 hover:text-cyan-600 cursor-pointer disabled:opacity-50"
                        >
                          <i className="ri-add-line"></i>
                          <span className="text-base">Add Sub Section</span>
                        </button>
                      </div>
                    )}
                  </div>
                ))}
                {isAdmin && (
                  <button
                    onClick={addSection}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-cyan-400 text-white rounded-lg hover:bg-cyan-500 transition-colors disabled:opacity-50"
                  >
                    <i className="ri-add-line"></i>
                    <span>Add Section</span>
                  </button>
                )}
                
                <div className="flex space-x-4">
                  <Button 
                    onClick={handleSave} 
                    variant="primary"
                    disabled={isLoading}
                    className="whitespace-nowrap"
                  >
                    {isLoading ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Saving...
                      </>
                    ) : (
                      'Save'
                    )}
                  </Button>
                  <Button onClick={() => navigate('/parser-creation')} variant="outline">
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Right Side - JSON Preview */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <JsonViewer key={jsonPreviewKey} data={generateJsonSchema()} />
            </div>
          </div>
        </div>
      </div>

      <MessageModal
        open={reservedModal.open}
        title="Reserved Format Name"
        message={reservedModal.message}
        variant="error"
        onClose={() => setReservedModal({ open: false, message: '' })}
      />
    </div>
  );
}
